// Data Lab · Linked-records panel — shows records related to the current
// document (orders/quotes/calls/person), grouped. Read-only cross-navigation.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Link2, ExternalLink, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { Panel } from "./_shared";

export default function LinkedRecords({ collection, id, testid = "lab-linked-records" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/datalab/links/${collection}/${id}`);
        if (alive) setData(data);
      } catch {
        if (alive) setData({ groups: [] });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [collection, id]);

  const total = data ? Object.values(data.counts || {}).reduce((a, b) => a + b, 0) : 0;

  return (
    <Panel icon={Link2} title="Linked records" right={loading ? "…" : `${total} linked`} testid={testid}>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Resolving links…
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(data.groups || []).map((g) => (
            <div key={g.key} data-testid={`${testid}-${g.key}`}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">
                {g.label} <span className="text-slate-600">· {g.items.length}</span>
              </div>
              {g.items.length === 0 ? (
                <div className="text-[12px] text-slate-600">None found.</div>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-auto pr-1">
                  {g.items.map((it) => (
                    <Link key={it.id} to={it.href}
                      className="flex items-center justify-between gap-2 rounded-lg border border-white/[0.08] bg-black/20 px-2.5 py-1.5 hover:bg-white/[0.05] group">
                      <div className="min-w-0">
                        <div className="text-[12px] text-slate-100 truncate">{it.label}</div>
                        {it.sub && <div className="text-[10px] font-mono text-slate-500 truncate">{it.sub}</div>}
                      </div>
                      <ExternalLink className="w-3 h-3 text-slate-600 group-hover:text-slate-300 shrink-0" />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {(!data.groups || data.groups.length === 0) && (
            <div className="text-[12px] text-slate-600">No linked records.</div>
          )}
        </div>
      )}
    </Panel>
  );
}
